import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SendIcon from "@mui/icons-material/Send";
import { format } from "date-fns";
import useUserReturns from "../hooks/useUserReturns";

// Helper to get chip color based on return status
const getReturnStatusChipColor = (status) => {
  // (Same function as in ReturnsPage)
  switch (status?.toLowerCase()) {
    case "requested":
      return "warning";
    case "under_review":
      return "info"; // Assuming admin might set this status
    case "approved":
    case "partially_approved":
      return "secondary";
    case "shipped_by_user":
      return "info";
    case "received":
      return "primary";
    case "completed":
      return "success";
    case "rejected":
      return "error";
    default:
      return "default";
  }
};

// Define return steps for the stepper
const getReturnSteps = (t) => {
  return [
    t("shared.returnSteps.requested"),
    t("shared.returnSteps.approved"),
    t("shared.returnSteps.shipped"),
    t("shared.returnSteps.received"),
    t("shared.returnSteps.refunded"),
  ];
};

// Map return status to step index
const getActiveStep = (status) => {
  // (Same function as in ReturnsPage)
  switch (status?.toLowerCase()) {
    case "requested":
      return 0;
    case "approved":
    case "partially_approved":
      return 1;
    case "shipped_by_user":
      return 2;
    case "received":
      return 3;
    case "completed":
      return 4; // Completed means refunded
    case "rejected":
      return 0; // Rejected stops at the start visually
    default:
      return -1; // Should not happen
  }
};

/**
 * User Return Detail Page component
 * Displays detailed information about a user's return and allows shipping actions
 * @returns {JSX.Element} The ReturnDetailPage component
 */
const ReturnDetailPage = () => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("returns");

  const { returnId } = useParams();
  const {
    currentReturn,
    status,
    error,
    actionStatus,
    actionError,
    loadReturnDetail,
    handleMarkAsShipped,
  } = useUserReturns();

  const [trackingInfo, setTrackingInfo] = useState({
    trackingNumber: "",
    shippingCarrier: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Load return details when component mounts or returnId changes
  useEffect(() => {
    let cleanup = () => {};
    if (returnId) {
      cleanup = loadReturnDetail(returnId);
    }
    // Ensure cleanup runs when component unmounts
    return cleanup;
  }, [returnId, loadReturnDetail]);

  // Handle input changes for tracking form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTrackingInfo((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate tracking form
  const validateForm = () => {
    const errors = {};
    if (!trackingInfo.trackingNumber?.trim()) {
      errors.trackingNumber = t("validation.required", { ns: "common" });
    }
    // Optionally add carrier validation
    // if (!trackingInfo.shippingCarrier?.trim()) {
    //   errors.shippingCarrier = t('validation.required', { ns: 'common' });
    // }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle submitting the tracking information
  const handleSubmitTracking = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    await handleMarkAsShipped({
      returnId,
      trackingInfo: {
        trackingNumber: trackingInfo.trackingNumber,
        shippingCarrier: trackingInfo.shippingCarrier,
      },
    });
    // No need to reset form here, the component will likely re-render
    // with the updated status, hiding the form.
  };

  // --- Render Logic ---

  if (status === "loading" || (!currentReturn && status !== "failed")) {
    return (
      <Container sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (status === "failed") {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          {error?.message || tCommon("status.error")}
        </Alert>
        <Box mt={2}>
          <Button
            component={RouterLink}
            to="/returns"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            {t("user.detail.backToReturns")}
          </Button>
        </Box>
      </Container>
    );
  }

  if (!currentReturn) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">{t("user.detail.returnNotFound")}</Alert>
        <Box mt={2}>
          <Button
            component={RouterLink}
            to="/returns"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            {t("user.detail.backToReturns")}
          </Button>
        </Box>
      </Container>
    );
  }

  const returnDetail = currentReturn;
  const steps = getReturnSteps(t);
  const activeStep = getActiveStep(returnDetail.status);
  const canMarkShipped = ["approved", "partially_approved"].includes(
    returnDetail.status
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          component={RouterLink}
          to="/returns"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{ mr: 2 }}
        >
          {t("user.detail.backToReturns")}
        </Button>
        <Typography variant="h4" component="h1">
          {t("user.detail.title")}
        </Typography>
        <Chip
          label={t(`shared.status.${returnDetail.status}`) || "Unknown"}
          color={getReturnStatusChipColor(returnDetail.status)}
          sx={{ ml: 2, textTransform: "capitalize" }}
        />
      </Box>

      {/* Status Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("user.detail.returnProgress")}
        </Typography>
        <Box sx={{ mt: 2, overflowX: "auto" }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ minWidth: "600px" }}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-iconContainer .Mui-active": {
                      color: "white", // White color for the number when active
                    },
                    "& .MuiStepLabel-iconContainer .Mui-active .MuiStepIcon-text":
                      {
                        fill: "white", // White color for the number inside the icon
                      },
                  }}
                  error={returnDetail.status === "rejected" && index === 1}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          {returnDetail.status === "rejected" && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <strong>{t("user.detail.returnRejected")}:</strong>{" "}
              {returnDetail.adminNotes || t("user.detail.noReasonProvided")}
            </Alert>
          )}
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Order & Return Info */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {t("user.detail.information.title")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={t("user.detail.information.returnId")}
                  secondary={returnDetail.id}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("user.detail.information.relatedOrder")}
                  secondary={
                    returnDetail.order?.orderNumber || returnDetail.orderId
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("user.detail.information.dateRequested")}
                  secondary={
                    returnDetail.createdAt
                      ? format(new Date(returnDetail.createdAt), "PPpp")
                      : "N/A"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("user.detail.information.yourReason")}
                  secondary={returnDetail.reason || "N/A"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("user.detail.information.yourNotes")}
                  secondary={returnDetail.notes || "None"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("user.detail.information.adminNotes")}
                  secondary={returnDetail.adminNotes || "None"}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Returned Items */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {t("user.detail.items.title")}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("user.detail.items.product")}</TableCell>
                    <TableCell align="center">
                      {t("user.detail.items.quantity")}
                    </TableCell>
                    <TableCell>{t("user.detail.items.decision")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returnDetail.items && returnDetail.items.length > 0 ? (
                    returnDetail.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          {item.orderItem?.product?.name || "N/A"}
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {item.adminDecision ||
                            t("user.detail.items.pendingReview")}
                          {item.adminDecision &&
                            ` (${item.adminQuantity || 0})`}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        {t("user.detail.items.noItems")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Action: Mark as Shipped */}
        {canMarkShipped && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("user.detail.shipping.title")}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("user.detail.shipping.instructions")}
              </Typography>
              <Box component="form" onSubmit={handleSubmitTracking} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      id="trackingNumber"
                      label={t("user.detail.shipping.trackingNumber")}
                      name="trackingNumber"
                      value={trackingInfo.trackingNumber}
                      onChange={handleInputChange}
                      error={!!formErrors.trackingNumber}
                      helperText={formErrors.trackingNumber}
                      disabled={actionStatus === "loading"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="shippingCarrier"
                      label={t("user.detail.shipping.shippingCarrier")}
                      name="shippingCarrier"
                      value={trackingInfo.shippingCarrier}
                      onChange={handleInputChange}
                      disabled={actionStatus === "loading"}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ textAlign: "right" }}>
                    {actionStatus === "failed" && actionError && (
                      <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                        {actionError?.message ||
                          t("user.detail.shipping.failedToMark")}
                      </Alert>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SendIcon />}
                      disabled={actionStatus === "loading"}
                    >
                      {actionStatus === "loading"
                        ? t("user.detail.shipping.submitting")
                        : t("user.detail.shipping.submitButton")}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Refund Info (if applicable) */}
        {(returnDetail.status === "completed" ||
          returnDetail.refundAmount > 0) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("user.detail.refund.title")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={t("user.detail.refund.status")}
                    secondary={
                      returnDetail.status === "completed"
                        ? t("user.detail.refund.completed")
                        : t("user.detail.refund.pending")
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t("user.detail.refund.amount")}
                    secondary={`CHF ${
                      typeof returnDetail.refundAmount === "string" ||
                      typeof returnDetail.refundAmount === "number"
                        ? parseFloat(returnDetail.refundAmount)?.toFixed(2) ??
                          "0.00"
                        : "0.00"
                    }`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t("user.detail.refund.method")}
                    secondary={returnDetail.refundMethod || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t("user.detail.refund.processedDate")}
                    secondary={
                      returnDetail.refundedAt
                        ? format(new Date(returnDetail.refundedAt), "PPpp")
                        : t("user.detail.refund.notYetProcessed")
                    }
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default ReturnDetailPage;
